<template>
    <div class="carrier-service-rate-dropdown">
        <md-menu md-direction="bottom-start" md-align-trigger :md-active.sync="toggleMenu">
            <div
                class="menu-trigger"
                tabindex="0"
                role="button"
                @click="openMenu"
                @keydown.enter.prevent="openMenu"
                @keydown.space.prevent="openMenu"
            >
                <template v-if="selectedRate">
                    <img :src="selectedRate.carrierLogoUrl" :alt="selectedRate.carrierName" class="carrier-logo" />
                    <div class="carrier-info">
                        <div class="carrier-name">{{ selectedRate.carrierName }} {{ selectedRate.serviceName }}</div>
                        <div class="carrier-details" v-if="selectedRate.amount > 0">
                            <span class="carrier-amount">
                                {{ formatAmount(selectedRate.amount, selectedRate.currency || '$') }}
                            </span>
                            <template v-if="selectedRate.estimatedDeliveryTime">
                                <span class="carrier-dot">•</span>
                                <span class="carrier-time">{{ selectedRate.estimatedDeliveryTime }}</span>
                            </template>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <span>Select a carrier service</span>
                </template>
            </div>
            <md-menu-content>
                <md-list>
                    <!--                    should handle when the list is empty -->
                    <md-list-item v-if="choices.length === 0" class="no-choices" disabled>
                        No carrier services available
                    </md-list-item>
                    <md-list-item
                        v-for="rate in choices"
                        :key="rate.identifier"
                        @click="selectRate(rate)"
                        :class="{ selected: selectedRate && selectedRate.identifier === rate.identifier }"
                    >
                        <img :src="rate.carrierLogoUrl" :alt="rate.carrierName" class="carrier-logo" />
                        <div class="carrier-info">
                            <div class="carrier-name">{{ rate.carrierName }} {{ rate.serviceName }}</div>
                            <div class="carrier-details" v-if="rate.amount > 0">
                                <span class="carrier-amount">
                                    {{ formatAmount(rate.amount, rate.currency || '$') }}
                                </span>
                                <template v-if="rate.estimatedDeliveryTime">
                                    <span class="carrier-dot">•</span>
                                    <span class="carrier-time">{{ rate.estimatedDeliveryTime }}</span>
                                </template>
                            </div>
                        </div>
                    </md-list-item>
                </md-list>
            </md-menu-content>
        </md-menu>
    </div>
</template>

<script>
/**
 * @typedef {Object} Rate
 * @property {string} serviceCode - The code or identifier for this carrier service.
 * @property {string} serviceName - The user-friendly name of the carrier service.
 * @property {number|null} amount - The amount the carrier service is charging for the shipment (includes tax).
 * @property {string} currency - The currency in which the amount is denominated.
 * @property {string} estimatedDeliveryTime - The estimated delivery time for the shipment.
 * @property {string} carrierName - The name of the carrier.
 * @property {string} carrierLogoUrl - The publicly-available URL to the carrier's logo.
 * @property {string} identifier
 * @property {number} teamCarrierId
 *
 */

/**
 * @typedef {Object} CarrierRate
 * @property {string} serviceCode - The code or identifier for this carrier service.
 * @property {string} serviceName - The user-friendly name of the carrier service.
 * @property {number|null} amount - The amount the carrier service is charging for the shipment (includes tax).
 * @property {string} currency - The currency in which the amount is denominated.
 * @property {string} estimatedDeliveryTime - The estimated delivery time for the shipment.
 */

/**
 * @typedef {Object} CarrierServiceRate
 * @property {string} carrierName - The name of the carrier.
 * @property {string} carrierLogoUrl - The publicly-available URL to the carrier's logo.
 * @property {CarrierRate[]} serviceRates - An array of carrier services available for the shipment.
 */

/**
 * @typedef {Object} AssignedCarrierChangedEvent
 * @property {number} teamCarrierId - The ID of the newly assigned carrier.
 * @property {string} carrierServiceCode - The code of the newly assigned carrier service.
 * @property {string} carrierServiceName
 */

export default {
    name: 'CarrierServiceRateDropdown',
    props: {
        /**
         * List of carrier service rates available for selection
         * @type {CarrierServiceRate[]}
         */
        carrierServiceRates: {
            type: Array,
            required: true,
            validator: (value) => {
                return value.every(
                    (item) =>
                        typeof item === 'object' &&
                        typeof item.carrierName === 'string' &&
                        typeof item.carrierLogoUrl === 'string' &&
                        Array.isArray(item.serviceRates)
                );
            }
        },
        /**
         * Currently assigned carrier service rate
         * @type {import("@/jsDocTypes/Carrier").CarrierJobDto |null}
         */
        assignedCarrierServiceRate: {
            type: Object,
            required: false,
            default: () => null
        },
        editable: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            selectedIdentifier: null,
            toggleMenu: false
        };
    },
    computed: {
        /**
         * @returns {Rate[]}
         */
        choices() {
            const flatList = [];
            this.carrierServiceRates.forEach((item) => {
                item.serviceRates.forEach((service) => {
                    flatList.push({
                        identifier: this.createIdentifier(item.teamCarrierId, service.serviceCode),
                        serviceCode: service.serviceCode,
                        serviceName: service.serviceName,
                        amount: service.amount,
                        currency: service.currency,
                        estimatedDeliveryTime: service.estimatedDeliveryTime,
                        carrierName: item.carrierName,
                        carrierLogoUrl: item.carrierLogoUrl,
                        teamCarrierId: item.teamCarrierId || null
                    });
                });
            });
            return flatList;
        },
        selectedRate() {
            return this.choices.find((rate) => rate.identifier === this.selectedIdentifier) || null;
        }
    },
    watch: {
        assignedCarrierServiceRate: {
            immediate: true,
            /**
             *
             * @param {CarrierJobDto | null} newVal
             */
            handler(newVal) {
                if (newVal) {
                    this.selectedIdentifier = this.createIdentifier(newVal.teamCarrierId, newVal.carrierServiceCode);
                } else {
                    this.selectedIdentifier = null;
                }
            }
        }
    },
    methods: {
        openMenu() {
            if (!this.editable) {
                return;
            }
            this.toggleMenu = !this.toggleMenu;
        },
        createIdentifier(teamCarrierId, serviceCode) {
            return `carriers/${teamCarrierId}/services/${serviceCode}`;
        },
        /**
         *
         * @param {Rate} rate
         */
        selectRate(rate) {
            this.selectedIdentifier = rate.identifier;
            /**
             * @type {AssignedCarrierChangedEvent}
             */
            const eventData = {
                teamCarrierId: rate.teamCarrierId,
                carrierServiceCode: rate.serviceCode,
                carrierServiceName: rate.serviceName
            };
            this.$emit('assignedCarrierChanged', eventData);
            this.toggleMenu = false;
        },
        formatAmount(amount, currency) {
            if (amount == null) {
                return `${currency} 0.00`;
            }
            return `${currency} ${amount.toFixed(2)}`;
        }
    },
    mounted() {
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.$el.contains(e.target)) {
                this.isOpen = false;
            }
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeDropdown);
    }
};
</script>

<style scoped lang="scss">
.carrier-service-rate-dropdown {
    width: 100%;
}

span {
    padding: 0 !important;
}

.menu-trigger {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 260px;
    background: #fff;
    border: none;
    border-radius: 8px;
    padding: 0;
    box-shadow: none;
    font-size: 15px;
    font-weight: 500;
    justify-content: flex-start;
    height: 56px;
    cursor: pointer;
    transition: background 0.15s;
}

.menu-trigger:focus,
.menu-trigger:hover {
    background: #f5f5f5;
}

.menu-trigger .carrier-logo {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    object-fit: contain;
    background: #fff;
    border: 1px solid #eee;
    margin-left: 4px;
}

.menu-trigger .carrier-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    line-height: 1.1;
}

.menu-trigger .carrier-name {
    font-weight: 700;
    font-size: 16px;
    color: #222;
    margin-bottom: 2px;
}

.menu-trigger .carrier-details {
    font-size: 13px;
    color: #888;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 400;
}

.menu-trigger .carrier-amount {
    font-size: 13px;
    color: #888;
    font-weight: 400;
}

.menu-trigger .carrier-dot {
    color: #bbb;
    font-size: 12px;
    margin: 0 2px;
}

.menu-trigger .carrier-carbon {
    color: #4caf50;
    font-weight: 500;
}

.md-menu-content {
    min-width: 400px;
    max-height: 400px;
    overflow-y: auto;
    padding: 0;
}

.md-list-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 16px;
    border-radius: 8px;
    margin: 4px 8px;
    cursor: pointer;
    transition: background 0.15s;
}

::v-deep .md-list-item-content {
    display: flex;
    gap: 15px;
    justify-content: flex-start;
    align-items: center;
}

.md-list-item.selected,
.md-list-item:hover {
    background: #f5f5f5;
}

.md-list-item .carrier-logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    object-fit: contain;
    background: #fff;
    border: 1px solid #eee;
}

.md-list-item .carrier-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    line-height: 1.1;
}

.md-list-item .carrier-name {
    font-weight: 700;
    font-size: 15px;
    color: #222;
    margin-bottom: 2px;
}

.md-list-item .carrier-details {
    font-size: 13px;
    color: #888;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 400;
}

.md-list-item .carrier-amount {
    font-size: 13px;
    color: #888;
    font-weight: 400;
}

.md-list-item .carrier-dot {
    color: #bbb;
    font-size: 12px;
    margin: 0 2px;
}

.md-list-item .carrier-carbon {
    color: #4caf50;
    font-weight: 500;
}
</style>
